import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Select,
  FormControl,
  Checkbox,
  TableSortLabel,
  Toolbar,
  Tabs,
  Tab,
  alpha,
  InputLabel,
  Autocomplete,
  useTheme,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Calculate as CalculateIcon,
  AutoAwesome as AIIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Layers as TierIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import { useBrand } from '../../../../context/BrandContext';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const TIER_COLORS = {
  1: 'success',
  2: 'warning',
  3: 'default'
};

const TIER_LABELS = {
  1: 'Tier 1 (Alto Impacto)',
  2: 'Tier 2 (Médio Impacto)',
  3: 'Tier 3 (Baixo Impacto)'
};

const TIER_SHORT_LABELS = {
  1: 'T1',
  2: 'T2',
  3: 'T3'
};

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'threads', label: 'Threads' }
];

// Lista de categorias (agora via API, mantido apenas como fallback inicial)
const CATEGORIES_FALLBACK = [
  'Automotivo', 'Tecnologia', 'Economia/Finanças', 'Entretenimento',
  'Esportes', 'Lifestyle', 'Política', 'Saúde', 'Educação',
  'Varejo/Comércio', 'Indústria', 'Agronegócio', 'Imobiliário',
  'Turismo', 'Moda/Beleza', 'Gastronomia', 'Outros'
];

const TierManagement = () => {
  const theme = useTheme();
  const { activeBrandData, isAdminOrMensure } = useBrand();

  const [initialLoading, setInitialLoading] = useState(true); // apenas no primeiro load
  const [refreshing, setRefreshing] = useState(false);        // refreshes subsequentes
  const [overrides, setOverrides] = useState([]);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [syncingGlobal, setSyncingGlobal] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recalcDialogOpen, setRecalcDialogOpen] = useState(false);
  const [selectedOverride, setSelectedOverride] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  // Sorting and selection states
  const [sortField, setSortField] = useState('count');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedSources, setSelectedSources] = useState([]);

  const getOverrideCategories = (override) => {
    const categories = Array.isArray(override?.categories)
      ? override.categories.filter(Boolean)
      : [];

    if (categories.length > 0) return categories;
    return override?.category ? [override.category] : [];
  };
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkTier, setBulkTier] = useState(2);
  const [aiCategorizing, setAiCategorizing] = useState(false);
  const [recalculatingSelected, setRecalculatingSelected] = useState(false);

  // Filter states
  const [filterSearch, setFilterSearch] = useState('');
  const [filterMediaType, setFilterMediaType] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterHasOverride, setFilterHasOverride] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [editingThresholds, setEditingThresholds] = useState(null);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, hasMore: false });
  const [loadingMore, setLoadingMore] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(''); // valor com delay para requests

  const [formData, setFormData] = useState({
    type: 'domain',
    domain: '',
    username: '',
    platform: 'instagram',
    tier: 2,
    reason: '',
    categories: []
  });

  // Fetch data — initialLoading bloqueia o render completo apenas no primeiro carregamento
  const isFirstLoad = React.useRef(true);
  
  const fetchSources = useCallback(async (page = 1, append = false, search = '') => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await api.get(`/tiers/sources?page=${page}&limit=50${searchParam}`);
      const newSources = response.data.data || [];
      const paginationData = response.data.pagination || { page, limit: 50, total: newSources.length, hasMore: false };
      
      setPagination(prev => ({
        page: paginationData.page,
        limit: paginationData.limit,
        total: paginationData.total,
        hasMore: paginationData.hasMore
      }));
      
      if (append) {
        setSources(prev => [...prev, ...newSources]);
      } else {
        setSources(newSources);
      }
      
      return newSources;
    } catch (err) {
      console.error('Erro ao carregar fontes:', err);
      setError('Erro ao carregar fontes');
      return [];
    }
  }, []);
  
  const fetchData = useCallback(async () => {
    if (isFirstLoad.current) {
      setInitialLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const [overridesRes, sourcesRes, statsRes, thresholdsRes, categoriesRes] = await Promise.all([
        api.get('/tiers/overrides'),
        api.get('/tiers/sources?page=1&limit=50'),
        api.get('/tiers/stats'),
        api.get('/tiers/thresholds'),
        api.get('/tiers/categories')
      ]);

      setOverrides(overridesRes.data.data || []);
      setSources(sourcesRes.data.data || []);
      setStats(statsRes.data.data || null);
      setThresholds(thresholdsRes.data.data || null);
      
      // Atualizar paginação
      if (sourcesRes.data.pagination) {
        setPagination(sourcesRes.data.pagination);
      }

      // Categorias - usar label como string para compatibilidade
      const cats = categoriesRes.data.data || [];
      setAvailableCategories(cats.map(c => typeof c === 'string' ? c : (c.value || c.label)));
      setEditingThresholds(JSON.parse(JSON.stringify(thresholdsRes.data.data || {})));
      setError('');
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados de tier');
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
      isFirstLoad.current = false;
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounce na busca: aguarda 400ms antes de disparar request ao servidor
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [filterSearch]);

  // Quando debouncedSearch muda, refazer busca no servidor (reseta para página 1)
  useEffect(() => {
    if (isFirstLoad.current) return; // Não disparar na montagem inicial (fetchData já faz isso)
    fetchSources(1, false, debouncedSearch);
  }, [debouncedSearch, fetchSources]);


  // Handlers
  const handleOpenDialog = (override = null) => {
    if (override) {
      const overrideCategories = getOverrideCategories(override);
      setSelectedOverride(override);
      setFormData({
        type: override.domain ? 'domain' : 'username',
        domain: override.domain || '',
        username: override.username || '',
        platform: override.platform || 'instagram',
        tier: override.tier,
        reason: override.reason || '',
        categories: overrideCategories
      });
    } else {
      setSelectedOverride(null);
      setFormData({
        type: 'domain',
        domain: '',
        username: '',
        platform: 'instagram',
        tier: 2,
        reason: '',
        categories: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOverride(null);
    setError('');
  };

  const handleSave = async () => {
    try {
      const payload = {
        tier: formData.tier,
        reason: formData.reason,
        categories: Array.isArray(formData.categories) ? formData.categories : []
      };

      if (formData.type === 'domain') {
        if (!formData.domain.trim()) {
          setError('Dominio e obrigatorio');
          return;
        }
        payload.domain = formData.domain.trim();
      } else {
        if (!formData.username.trim()) {
          setError('Username e obrigatorio');
          return;
        }
        payload.username = formData.username.trim();
        payload.platform = formData.platform;
      }

      if (selectedOverride) {
        await api.put(`/tiers/overrides/${selectedOverride._id}`, payload);
        setSuccess('Regra atualizada!');
      } else {
        await api.post('/tiers/overrides', payload);
        setSuccess('Regra criada!');
      }

      handleCloseDialog();
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar override');
    }
  };

  const handleDelete = async () => {
    if (!selectedOverride) return;

    try {
      await api.delete(`/tiers/overrides/${selectedOverride._id}`);
      setSuccess('Regra excluída!');
      setDeleteDialogOpen(false);
      setSelectedOverride(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao excluir override');
      setDeleteDialogOpen(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const response = await api.post('/tiers/recalculate');
      setSuccess(`Recalculo concluido: ${response.data.data.updated}/${response.data.data.total} artigos`);
      setRecalcDialogOpen(false);
      fetchData();
      // Notificar a lista de artigos para invalidar o cache
      window.dispatchEvent(new CustomEvent('tier:recalculated'));
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao recalcular tiers');
    } finally {
      setRecalculating(false);
    }
  };

  const openDeleteDialog = (override) => {
    setSelectedOverride(override);
    setDeleteDialogOpen(true);
  };

  const handleQuickOverride = (source) => {
    setFormData({
      type: 'domain',
      domain: source.domain || '',
      username: '',
      platform: 'instagram',
      tier: 2,
      reason: '',
      categories: getOverrideCategories(source.override)
    });
    setDialogOpen(true);
  };

  const handleDialogCategoriesChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      categories: Array.isArray(newValue) ? newValue.filter(Boolean) : []
    }));
  };

  // Helper para identificar o grupo de mídia baseado na plataforma
  const getPlatformGroup = useCallback((platform, matrix) => {
    if (!platform || !matrix) return 'online_portal';
    
    const normalizedPlatform = platform.toLowerCase();

    // Tentar encontrar o grupo que contém esta plataforma
    for (const groupKey in matrix) {
        const group = matrix[groupKey];
        if (group && group.platforms && group.platforms.some(p => p.toLowerCase() === normalizedPlatform)) {
            return groupKey;
        }
    }
    
    // Fallback: Se for rede social mas não apareceu na lista, tratar como social_mass
    if (['instagram', 'facebook', 'tiktok', 'youtube'].includes(normalizedPlatform)) return 'social_mass';
    if (['twitter', 'x', 'threads', 'linkedin'].includes(normalizedPlatform)) return 'social_info';
    
    return 'online_portal'; // Default para sites e outros
  }, []);

  // Calcular tier automático baseado nos thresholds dinâmicos da Matrix
  const calculateAutoTier = useCallback((metricValue, platform) => {
    if (!thresholds || !activeBrandData) return 3;

    const groupKey = getPlatformGroup(platform, thresholds);
    const group = thresholds[groupKey] || thresholds['online_portal'];
    
    if (!group || !group.thresholds) return 3;

    const profile = activeBrandData.tierProfile || 'medium';
    const profileThresholds = group.thresholds[profile] || group.thresholds['medium'] || 10000;
    
    let t1, t2;

    // Suporte a formato antigo (Número) e novo (Objeto)
    if (typeof profileThresholds === 'object') {
      t1 = profileThresholds.t1 || 10000;
      t2 = profileThresholds.t2 || Math.round(t1 * 0.1);
    } else {
      t1 = Number(profileThresholds);
      t2 = Math.round(t1 * 0.1);
    }

    if (metricValue >= t1) return 1;
    if (metricValue >= t2) return 2;
    return 3;
  }, [thresholds, activeBrandData, getPlatformGroup]);

  // Verificar se a categoria recebe boost de nicho (Tier Trade)
  const hasCategoryBoost = useCallback((category) => {
    if (!category || !activeBrandData?.relevantCategories) return false;
    return activeBrandData.relevantCategories.includes(category);
  }, [activeBrandData]);

  const [savingCategory, setSavingCategory] = useState(false);

  // Troca rapida de tier (cria ou atualiza override) - com atualização otimista
  // Helper: Calcular tier efetivo (boost de categoria > override manual > auto por audiência)
  const getEffectiveTier = useCallback((source) => {
    // 1. Verificar boost de categoria (maior prioridade)
    const cats = Array.isArray(source.globalCategories || source.categories)
      ? (source.globalCategories || source.categories)
      : (source.globalCategory ? [source.globalCategory] : []);
    const hasBoost = cats.some(c => hasCategoryBoost(c));
    if (hasBoost) return 1;

    // 2. Override manual
    if (source.hasOverride && source.override?.tier) return source.override.tier;

    // 3. Tier auto por audiência
    return calculateAutoTier(source.maxSubscribers || 0, source.mediaType);
  }, [hasCategoryBoost, calculateAutoTier]);

  // Handlers
  const handleCategoryChange = async (source, newCategories) => {
    try {
      setSavingCategory(true);
      const isRecord = source.hasOverride;
      const cats = Array.isArray(newCategories) ? newCategories : (newCategories ? [newCategories] : []);
      const categoryBoost = cats.some((cat) => hasCategoryBoost(cat));

      if (isRecord) {
        await api.put(`/tiers/overrides/${source.override._id}`, {
          categories: cats,
          category: cats[0] || ''
        });
      } else {
        await api.post('/tiers/overrides', {
          domain: !source.username ? source.domain : undefined,
          username: source.username,
          platform: source.mediaType?.toLowerCase(),
          tier: categoryBoost ? 1 : calculateAutoTier(source.maxSubscribers, source.mediaType),
          categories: cats,
          category: cats[0] || '',
          reason: 'Atribuição Manual'
        });
      }

      setSuccess('Categorias atualizadas!');
      fetchData();
    } catch (err) {
      setError('Erro ao atualizar categorias');
    } finally {
      setSavingCategory(false);
    }
  };


  const handleSyncGlobal = async () => {
    if (!window.confirm('Deseja sincronizar as categorias das suas fontes com a Base Global da Mensure? Isso substituirá categorias identificadas por IA.')) {
      return;
    }

    setSyncingGlobal(true);
    try {
      const res = await api.post('/tiers/sync-global-categories');
      setSuccess(res.data.message || 'Sincronização concluída');
      fetchData();
      // Notificar a lista de artigos para invalidar o cache
      window.dispatchEvent(new CustomEvent('tier:recalculated'));
    } catch (err) {
      console.error('Erro na sincronização global:', err);
      setError('Erro ao sincronizar com a base global');
    } finally {
      setSyncingGlobal(false);
    }
  };

  const handleQuickTierChange = async (source, newTier) => {
    // Para redes sociais, usar username como identificador unico
    const isSocialPlatform = ['Instagram', 'TikTok', 'Twitter', 'YouTube', 'Facebook', 'LinkedIn'].includes(source.mediaType);

    // Encontrar source pelo identificador correto
    const sourceIndex = sources.findIndex(s => {
      if (isSocialPlatform && source.username) {
        return s.username === source.username && s.mediaType === source.mediaType;
      }
      return s.domain === source.domain && s.siteName === source.siteName;
    });
    if (sourceIndex === -1) return;

    // Guardar estado anterior para reverter em caso de erro
    const previousSources = [...sources];
    const previousOverrides = [...overrides];

    // Atualização otimista - atualiza UI imediatamente
    const updatedSources = [...sources];
    updatedSources[sourceIndex] = {
      ...updatedSources[sourceIndex],
      hasOverride: true,
      override: {
        ...updatedSources[sourceIndex].override,
        _id: updatedSources[sourceIndex].override?._id || 'temp',
        tier: newTier
      }
    };
    setSources(updatedSources);

    try {
      // Construir payload baseado no tipo de fonte
      // Preservar categoria existente se houver
      const existingCategories = getOverrideCategories(source.override);
      const existingCategory = existingCategories[0];
      const existingCategoryConfidence = source.override?.categoryConfidence;
      const existingCategorySetByAI = source.override?.categorySetByAI;
      const sourceCategories = Array.isArray(source.globalCategories || source.categories)
        ? (source.globalCategories || source.categories).filter(Boolean)
        : (source.globalCategory ? [source.globalCategory] : []);
      const categoriesForCreate = existingCategories.length > 0 ? existingCategories : sourceCategories;

      let payload;
      if (isSocialPlatform && source.username) {
        // Para redes sociais: usar username + platform
          payload = {
            username: source.username,
            platform: source.mediaType.toLowerCase(),
            tier: newTier,
            reason: 'Ajustado manualmente',
            // Preservar categoria se existir
            categories: categoriesForCreate,
            ...(existingCategory && {
              category: existingCategory,
              categoryConfidence: existingCategoryConfidence,
              categorySetByAI: existingCategorySetByAI
            })
        };
      } else {
        // Para websites: usar domain
          payload = {
            domain: source.domain,
            tier: newTier,
            reason: 'Ajustado manualmente',
            // Preservar categoria se existir
            categories: categoriesForCreate,
            ...(existingCategory && {
              category: existingCategory,
              categoryConfidence: existingCategoryConfidence,
              categorySetByAI: existingCategorySetByAI
            })
        };
      }

      let createdNewOverride = false;

      if (source.hasOverride && source.override?._id) {
        // Tentar atualizar override existente
        try {
          await api.put(`/tiers/overrides/${source.override._id}`, { tier: newTier });
        } catch (putErr) {
          // Se o override não existe mais (404), criar um novo
          if (putErr.response?.status === 404) {
            console.log('[TierManagement] Override não existe mais, criando novo...');
            createdNewOverride = true;
          } else {
            throw putErr;
          }
        }
      }

      // Criar novo override se não existia ou se o PUT retornou 404
      if (!source.hasOverride || !source.override?._id || createdNewOverride) {
        const response = await api.post('/tiers/overrides', payload);
        // Atualizar com o ID real do override criado
        updatedSources[sourceIndex].override = {
          _id: response.data.data._id,
          tier: newTier,
          category: existingCategory,
          categories: categoriesForCreate,
          categoryConfidence: existingCategoryConfidence,
          categorySetByAI: existingCategorySetByAI
        };
        updatedSources[sourceIndex].hasOverride = true;
        setSources(updatedSources);

        // Adicionar aos overrides
        setOverrides(prev => [...prev.filter(o => o._id !== source.override?._id), response.data.data]);
      }

      setSuccess('Tier atualizado!');
      setTimeout(() => setSuccess(''), 2000);

      // Se criou novo override, fazer refresh para sincronizar estado
      if (createdNewOverride) {
        fetchData();
      }
    } catch (err) {
      // Reverter em caso de erro
      setSources(previousSources);
      setOverrides(previousOverrides);
      setError(err.response?.data?.message || 'Erro ao alterar tier');
    }
  };

  // Remover personalizacao (volta ao automatico) - com atualização otimista
  const handleRemoveOverride = async (source) => {
    if (!source.hasOverride || !source.override?._id) return;

    // Para redes sociais, usar username como identificador unico
    const isSocialPlatform = ['Instagram', 'TikTok', 'Twitter', 'YouTube', 'Facebook', 'LinkedIn'].includes(source.mediaType);
    const sourceIndex = sources.findIndex(s => {
      if (isSocialPlatform && source.username) {
        return s.username === source.username && s.mediaType === source.mediaType;
      }
      return s.domain === source.domain && s.siteName === source.siteName;
    });
    if (sourceIndex === -1) return;

    // Guardar estado anterior para reverter em caso de erro
    const previousSources = [...sources];
    const previousOverrides = [...overrides];
    const overrideId = source.override._id;

    // Atualização otimista - remove da UI imediatamente
    const updatedSources = [...sources];
    updatedSources[sourceIndex] = {
      ...updatedSources[sourceIndex],
      hasOverride: false,
      override: null
    };
    setSources(updatedSources);
    setOverrides(overrides.filter(o => o._id !== overrideId));

    try {
      await api.delete(`/tiers/overrides/${overrideId}`);
      setSuccess('Voltou ao automático!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      // Reverter em caso de erro
      setSources(previousSources);
      setOverrides(previousOverrides);
      setError(err.response?.data?.message || 'Erro ao remover personalização');
    }
  };

  // Sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get unique media types for filter dropdown
  const uniqueMediaTypes = useMemo(() => {
    const types = new Set(sources.map(s => s.mediaType).filter(Boolean));
    return Array.from(types).sort();
  }, [sources]);

  // Get unique categories for filter dropdown
  const uniqueCategories = useMemo(() => {
      const cats = new Set(sources.flatMap(s => getOverrideCategories(s.override)));
      return Array.from(cats).sort();
  }, [sources]);

  const sortedSources = useMemo(() => {
    // First filter
    let filtered = [...sources];

    // Nota: filtro de busca é feito no servidor (server-side search via ?search=)
    // Apenas filtros locais são aplicados aqui (tipo, tier, categoria, override)

    // Media type filter
    if (filterMediaType) {
      filtered = filtered.filter(s => s.mediaType === filterMediaType);
    }

    // Tier filter
    if (filterTier) {
      const tierNum = Number(filterTier);
      filtered = filtered.filter(s => {
        const currentTier = getEffectiveTier(s);
        return currentTier === tierNum;
      });
    }

    // Category filter
    if (filterCategory) {
      if (filterCategory === '__none__') {
        filtered = filtered.filter(s => getOverrideCategories(s.override).length === 0);
      } else {
        filtered = filtered.filter(s => getOverrideCategories(s.override).includes(filterCategory));
      }
    }

    // Has override filter
    if (filterHasOverride) {
      if (filterHasOverride === 'yes') {
        filtered = filtered.filter(s => s.hasOverride);
      } else {
        filtered = filtered.filter(s => !s.hasOverride);
      }
    }

    // Then sort
    const sorted = filtered.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'siteName':
          aVal = (a.siteName || a.domain || '').toLowerCase();
          bVal = (b.siteName || b.domain || '').toLowerCase();
          break;
        case 'mediaType':
          aVal = (a.mediaType || '').toLowerCase();
          bVal = (b.mediaType || '').toLowerCase();
          break;
        case 'count':
          aVal = a.count || 0;
          bVal = b.count || 0;
          break;
        case 'maxSubscribers':
          aVal = a.maxSubscribers || 0;
          bVal = b.maxSubscribers || 0;
          break;
        case 'category':
          aVal = getOverrideCategories(a.override).join(', ').toLowerCase();
          bVal = getOverrideCategories(b.override).join(', ').toLowerCase();
          break;
        case 'lastArticleDate':
          aVal = a.lastArticleDate ? new Date(a.lastArticleDate).getTime() : 0;
          bVal = b.lastArticleDate ? new Date(b.lastArticleDate).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [sources, sortField, sortDirection, filterSearch, filterMediaType, filterTier, filterCategory, filterHasOverride, calculateAutoTier]);

  // Selection handlers - usar chave única para cada fonte (domain::siteName)
  const getSourceKey = (source) => {
    // Para redes sociais, usar domain + siteName para diferenciar perfis
    if (source.siteName && source.domain) {
      return `${source.domain}::${source.siteName}`;
    }
    return source.domain;
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allKeys = sortedSources.filter(s => s.domain).map(s => getSourceKey(s));
      setSelectedSources(allKeys);
    } else {
      setSelectedSources([]);
    }
  };

  const handleSelectSource = (source) => {
    const key = getSourceKey(source);
    setSelectedSources(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const isSelected = (source) => selectedSources.includes(getSourceKey(source));

  // Helper: converter chaves selecionadas em fontes
  const getSelectedSourceObjects = () => {
    return selectedSources.map(key => {
      return sortedSources.find(s => getSourceKey(s) === key);
    }).filter(Boolean);
  };

  // Helper: extrair identificadores para o backend
  const getSourceIdentifiers = (sources) => {
    const domains = [];
    const profiles = [];

    sources.forEach(source => {
      if (!source) return;

      const isSocialMedia = ['YouTube', 'Instagram', 'TikTok', 'Twitter', 'Facebook', 'LinkedIn'].includes(source.mediaType);

      // Para redes sociais, extrair username do campo username ou siteName
      if (isSocialMedia) {
        // Tentar obter username de várias fontes
        let username = source.username;

        // Se não tem username, tentar extrair do siteName
        // Mas só se o siteName parecer um username válido (sem espaços, sem |, etc)
        if (!username && source.siteName) {
          const cleanedSiteName = source.siteName.replace(/^@/, '').toLowerCase().trim();
          // Username válido: sem espaços, sem pipes, sem caracteres especiais além de _ e .
          const isValidUsername = /^[a-z0-9_.]+$/.test(cleanedSiteName);
          if (isValidUsername) {
            username = cleanedSiteName;
          } else {
            console.warn(`[getSourceIdentifiers] siteName não é username válido: "${source.siteName}"`);
          }
        }

        if (username) {
          profiles.push({
            username: username.toLowerCase(),
            platform: source.mediaType.toLowerCase()
          });
          console.log(`[getSourceIdentifiers] Perfil social: ${source.mediaType}/@${username}`);
        } else {
          // Rede social sem username identificado - não enviar como domain!
          console.warn(`[getSourceIdentifiers] Rede social sem username: ${source.mediaType} - ${source.siteName}`);
        }
      } else if (source.domain) {
        // Para sites normais (não redes sociais)
        domains.push(source.domain);
        console.log(`[getSourceIdentifiers] Domain: ${source.domain}`);
      }
    });

    return { domains, profiles };
  };

  // Bulk actions
  const handleBulkTierChange = async () => {
    if (selectedSources.length === 0) return;

    try {
      const sourceObjects = getSelectedSourceObjects();
      const { domains, profiles } = getSourceIdentifiers(sourceObjects);

      const payload = {
        ...(domains.length > 0 && { domains }),
        ...(profiles.length > 0 && { profiles }),
        ...(bulkTier && { tier: bulkTier })
      };

      await api.post('/tiers/bulk-update', payload);

      setSuccess(`${selectedSources.length} fontes atualizadas!`);
      setBulkDialogOpen(false);
      setSelectedSources([]);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao atualizar em massa');
    }
  };

  // AI Categorization
  const handleAICategorize = async () => {
    let sourcesToProcess;

    console.log('[TierManagement] === AI CATEGORIZE ===');
    console.log('[TierManagement] selectedSources:', selectedSources);

    if (selectedSources.length > 0) {
      sourcesToProcess = getSelectedSourceObjects();
      console.log('[TierManagement] Usando fontes selecionadas:', sourcesToProcess.map(s => ({
        domain: s?.domain,
        username: s?.username,
        siteName: s?.siteName,
        mediaType: s?.mediaType
      })));
    } else {
      sourcesToProcess = sortedSources.filter(s => s.domain && getOverrideCategories(s.override).length === 0);
      console.log('[TierManagement] Nenhuma seleção - usando todas sem categoria');
    }

    if (sourcesToProcess.length === 0) {
      setError('Nenhuma fonte para categorizar');
      return;
    }

    const { domains, profiles } = getSourceIdentifiers(sourcesToProcess.slice(0, 20));

    console.log('[TierManagement] Payload para API:', { domains, profiles });

    setAiCategorizing(true);
    try {
      const response = await api.post('/tiers/categorize-ai', {
        ...(domains.length > 0 && { domains }),
        ...(profiles.length > 0 && { profiles })
      });

      console.log('[TierManagement] Resposta da API:', response.data);

      setSuccess(`${response.data.data.categories.length} fontes categorizadas pela IA!`);
      setSelectedSources([]);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao categorizar com IA');
    } finally {
      setAiCategorizing(false);
    }
  };

  // Recalcular tiers das fontes selecionadas
  const handleRecalculateSelected = async () => {
    if (selectedSources.length === 0) {
      setError('Nenhuma fonte selecionada');
      return;
    }

    const sourceObjects = getSelectedSourceObjects();
    const { domains, profiles } = getSourceIdentifiers(sourceObjects);

    if (domains.length === 0 && profiles.length === 0) {
      setError('Nenhuma fonte válida para recalcular');
      return;
    }

    setRecalculatingSelected(true);
    try {
      const response = await api.post('/tiers/recalculate-sources', {
        domains,
        profiles
      });

      setSuccess(`${response.data.data.updated}/${response.data.data.total} artigos recalculados!`);
      // Notificar a lista de artigos para invalidar o cache
      window.dispatchEvent(new CustomEvent('tier:recalculated'));
      setSelectedSources([]);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao recalcular tiers');
    } finally {
      setRecalculatingSelected(false);
    }
  };

  // Funções para edição da matriz de tiers
  const handleThresholdChange = (groupKey, profile, tierKey, value) => {
    setEditingThresholds(prev => {
        if (!prev || !prev[groupKey]) return prev;
        
        const group = prev[groupKey];
        const currentProfile = group.thresholds ? group.thresholds[profile] : null;
        
        let newProfileValue;
        if (currentProfile && typeof currentProfile === 'object') {
            newProfileValue = { ...currentProfile, [tierKey]: value };
        } else {
            // Converter do formato antigo (Number) para o novo (Object)
            const baseValue = currentProfile ? Number(currentProfile) : 0;
            newProfileValue = { 
                t1: tierKey === 't1' ? value : baseValue,
                t2: tierKey === 't2' ? value : Math.round(baseValue * 0.1)
            };
        }

        return {
            ...prev,
            [groupKey]: {
                ...group,
                thresholds: {
                    ...(group.thresholds || {}),
                    [profile]: newProfileValue
                }
            }
        };
    });
  };

  const handleSaveThresholds = async () => {
    setSavingThresholds(true);
    try {
        const response = await api.post('/tiers/thresholds', { matrix: editingThresholds });
        setThresholds(response.data.data);
        setSuccess('Matriz de tiers atualizada com sucesso!');
        setTimeout(() => setSuccess(''), 3000);
        // Refresh data to update auto-tiers in list
        fetchData();
    } catch (err) {
        setError(err.response?.data?.message || 'Erro ao salvar matriz');
    } finally {
        setSavingThresholds(false);
    }
  };

  // Render — skeleton durante o primeiro carregamento
  if (initialLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Skeleton variant="text" width={240} height={36} />
            <Skeleton variant="text" width={320} height={20} sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton variant="rounded" width={160} height={36} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[1,2,3].map(i => (
            <Skeleton key={i} variant="rounded" width={180} height={90} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
        {[1,2,3,4,5,6,7,8].map(i => (
          <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Barra de progresso sutil durante refreshes (ex: apos salvar categoria) */}
      {refreshing && (
        <LinearProgress
          sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: 3 }}
        />
      )}
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Gerenciamento de Tiers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administre regras de classificação e escalas por plataforma
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          {isAdminOrMensure && (
            <>
              <Button
                variant="outlined"
                startIcon={<CalculateIcon />}
                onClick={() => setRecalcDialogOpen(true)}
              >
                Recalcular Tiers
              </Button>
              <Button
                variant="outlined"
                startIcon={aiCategorizing ? <CircularProgress size={20} /> : <AIIcon />}
                onClick={handleAICategorize}
                disabled={aiCategorizing || refreshing}
                sx={{ mr: 1 }}
              >
                Auto Categorizar
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Nova Regra
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Listagem de Fontes" />
          {isAdminOrMensure && <Tab label="Configuração de Escalas" />}
        </Tabs>
      </Box>

      {/* Aba 1: Listagem de Fontes */}
      {activeTab === 0 && (
        <>
          {/* Stats + Thresholds */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {stats && (
                <>
                  <Chip size="small" label={`${stats.tier1?.count || 0} T1`} color="success" sx={{ fontWeight: 600 }} />
                  <Chip size="small" label={`${stats.tier2?.count || 0} T2`} color="warning" sx={{ fontWeight: 600 }} />
                  <Chip size="small" label={`${stats.tier3?.count || 0} T3`} variant="outlined" sx={{ fontWeight: 600 }} />
                  <Typography variant="body2" color="text.secondary">|</Typography>
                  <Typography variant="body2"><strong>{stats.total || 0}</strong> artigos</Typography>
                </>
              )}
            </Box>

            {/* Filtros */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Buscar fonte..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                sx={{ minWidth: 200, flexGrow: 1 }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={filterMediaType}
                  onChange={(e) => setFilterMediaType(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Todos os Tipos</MenuItem>
                  {uniqueMediaTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={filterTier}
                  onChange={(e) => setFilterTier(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Todos Tiers</MenuItem>
                  <MenuItem value="1">Tier 1</MenuItem>
                  <MenuItem value="2">Tier 2</MenuItem>
                  <MenuItem value="3">Tier 3</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(filterSearch || filterMediaType || filterTier) && (
                  <Button size="small" onClick={() => {
                    setFilterSearch('');
                    setFilterMediaType('');
                    setFilterTier('');
                  }}>
                    Limpar
                  </Button>
                )}
                {selectedSources.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={syncingGlobal ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                      onClick={handleSyncGlobal}
                      disabled={syncingGlobal || refreshing}
                      sx={{ mr: 1 }}
                    >
                      Sincronizar Base Global
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={aiCategorizing ? <CircularProgress size={16} /> : <AIIcon />}
                      onClick={handleAICategorize}
                      disabled={aiCategorizing}
                    >
                      Categorizar IA
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setBulkDialogOpen(true)}
                    >
                      Massa ({selectedSources.length})
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Tabela */}
          <Paper elevation={0} variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedSources.length > 0 && selectedSources.length < sortedSources.length}
                        checked={sortedSources.length > 0 && selectedSources.length === sortedSources.filter(s => s.domain).length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'siteName'}
                        direction={sortField === 'siteName' ? sortDirection : 'asc'}
                        onClick={() => handleSort('siteName')}
                      >
                        Fonte / Veículo
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="center">Audiência</TableCell>
                    <TableCell align="center">Categorias</TableCell>
                    <TableCell align="center">Tier Atual</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSources.map((source, index) => {
                    const currentTier = getEffectiveTier(source);
                    const isCustomized = source.hasOverride;
                    const isItemSelected = isSelected(source);

                    return (
                      <TableRow 
                        key={index} 
                        hover 
                        selected={isItemSelected}
                        sx={{ 
                          '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                          '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleSelectSource(source)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>
                            {source.siteName || source.domain || '-'}
                          </Typography>
                          {source.domain && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {source.domain}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={source.mediaType || 'web'} 
                            size="small" 
                            variant="outlined" 
                            sx={{ 
                              fontSize: '0.65rem', 
                              fontWeight: 800, 
                              textTransform: 'uppercase',
                              height: 20,
                              borderColor: alpha(theme.palette.text.primary, 0.2)
                            }} 
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {source.maxSubscribers?.toLocaleString('pt-BR') || '0'}
                          </Typography>
                        </TableCell>

                        {/* Categorias - somente leitura */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                            {(() => {
                              const cats = Array.isArray(source.globalCategories || source.categories)
                                ? (source.globalCategories || source.categories)
                                : (source.globalCategory ? [source.globalCategory] : []);
                              if (cats.length === 0) {
                                return <Typography variant="caption" color="text.disabled">Sem categoria</Typography>;
                              }
                              return cats.slice(0, 2).map((cat, i) => (
                                <Chip 
                                  key={i} 
                                  label={cat} 
                                  size="small" 
                                  variant="filled"
                                  sx={{ 
                                    fontSize: '0.65rem', 
                                    height: 18, 
                                    fontWeight: 700,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: 'primary.main'
                                  }} 
                                />
                              ));
                            })()}
                            {(() => {
                               const cats = Array.isArray(source.globalCategories || source.categories)
                                 ? (source.globalCategories || source.categories)
                                 : (source.globalCategory ? [source.globalCategory] : []);
                               return cats.length > 2 && (
                                 <Tooltip title={cats.slice(2).join(', ')}>
                                   <Chip label={`+${cats.length - 2}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                 </Tooltip>
                               );
                            })()}
                          </Box>
                        </TableCell>

                        {/* Tier Atual - efetivo pelas regras */}
                        <TableCell align="center">
                          {(() => {
                            const effectiveTier = getEffectiveTier(source);
                            const cats = Array.isArray(source.globalCategories || source.categories)
                              ? (source.globalCategories || source.categories)
                              : (source.globalCategory ? [source.globalCategory] : []);
                            const hasBoost = cats.some(c => hasCategoryBoost(c));
                            const isManualOverride = source.hasOverride && !hasBoost;

                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                  <Select
                                    value={effectiveTier}
                                    onChange={(e) => handleQuickTierChange(source, Number(e.target.value))}
                                    size="small"
                                    disabled={hasBoost}
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: 28,
                                      fontWeight: 800,
                                      bgcolor: effectiveTier === 1
                                        ? (hasBoost ? 'success.dark' : 'success.main')
                                        : effectiveTier === 2 ? 'warning.main' : 'grey.500',
                                      color: 'white',
                                      borderRadius: 1.5,
                                      '& .MuiSvgIcon-root': { color: 'white' },
                                      '& fieldset': { border: 'none' }
                                    }}
                                  >
                                    <MenuItem value={1} sx={{ fontWeight: 700 }}>Tier 1</MenuItem>
                                    <MenuItem value={2} sx={{ fontWeight: 700 }}>Tier 2</MenuItem>
                                    <MenuItem value={3} sx={{ fontWeight: 700 }}>Tier 3</MenuItem>
                                  </Select>
                                </FormControl>
                                
                                {hasBoost && (
                                  <Tooltip title="Tier 1 garantido por Nicho (Categoria Relevante)">
                                    <Box 
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        color: 'success.main',
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        gap: 0.5
                                      }}
                                    >
                                      <CalculateIcon sx={{ fontSize: 14 }} />
                                      <Typography variant="caption" fontWeight={800} sx={{ fontSize: '0.65rem' }}>NICHO</Typography>
                                    </Box>
                                  </Tooltip>
                                )}

                                {isManualOverride && (
                                  <Tooltip title="Remover override manual e voltar ao automático">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleRemoveOverride(source)}
                                      sx={{ 
                                        color: 'error.main', 
                                        bgcolor: alpha(theme.palette.error.main, 0.05),
                                        '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
                                      }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Carregar mais: só quando não há busca ativa (busca já retorna todos os resultados do servidor) */}
                  {pagination.hasMore && !filterSearch && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Button 
                          variant="outlined" 
                          onClick={async () => {
                            setLoadingMore(true);
                            await fetchSources(pagination.page + 1, true, debouncedSearch);
                            setLoadingMore(false);
                          }}
                          disabled={loadingMore}
                        >
                          {loadingMore ? 'Carregando...' : `Carregar mais (${pagination.total - sources.length} restantes)`}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {activeTab === 1 && (
        <Box sx={{ p: 1 }}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Calibração de Escalas de Alcance
            </Typography>
            <Typography variant="body2">
              Defina os limites de <strong>Tier 1</strong> e <strong>Tier 2</strong> para cada porte de marca. 
              O sistema classifica automaticamente como <strong>Tier 3</strong> qualquer fonte com audiência abaixo do limite de Tier 2.
            </Typography>
          </Alert>

          <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="medium">
                <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, width: '30%' }}>GRUPO / PLATAFORMAS</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>PEQUENA</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>MÉDIA</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>GRANDE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editingThresholds && Object.entries(editingThresholds).map(([key, group]) => (
                    <TableRow key={key} hover>
                      <TableCell sx={{ py: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box 
                            sx={{ 
                              p: 1, 
                              borderRadius: 1.5, 
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              display: 'flex'
                            }}
                          >
                            <TierIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {key.replace('_', ' ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {group.platforms.join(', ')} • <strong>{group.metric}</strong>
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {['small', 'medium', 'large'].map(profile => {
                        const val = group.thresholds[profile] || { t1: 0, t2: 0 };
                        const t1Value = typeof val === 'object' ? val.t1 : val;
                        const t2Value = typeof val === 'object' ? val.t2 : Math.round(val * 0.1);
                        
                        return (
                          <TableCell key={profile} align="center">
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', px: 1 }}>
                              <TextField
                                type="number"
                                size="small"
                                label="Tier 1 (>=)"
                                value={t1Value}
                                onChange={(e) => handleThresholdChange(key, profile, 't1', Number(e.target.value))}
                                sx={{ 
                                  width: 130,
                                  '& .MuiInputBase-root': { fontWeight: 700, color: 'success.main' }
                                }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <TextField
                                type="number"
                                size="small"
                                label="Tier 2 (>=)"
                                value={t2Value}
                                onChange={(e) => handleThresholdChange(key, profile, 't2', Number(e.target.value))}
                                sx={{ 
                                  width: 130,
                                  '& .MuiInputBase-root': { fontWeight: 700, color: 'warning.main' }
                                }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <Box sx={{ 
                                width: 130, 
                                py: 0.5, 
                                px: 1, 
                                borderRadius: 1, 
                                bgcolor: alpha(theme.palette.text.disabled, 0.1),
                                border: '1px dashed',
                                borderColor: 'divider',
                                textAlign: 'left'
                              }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                                  Tier 3 (Resto)
                                </Typography>
                                <Typography variant="caption" fontWeight={800}>
                                  &lt; {t2Value.toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end', bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
              <Button 
                variant="contained" 
                size="large"
                onClick={handleSaveThresholds} 
                disabled={savingThresholds}
                startIcon={savingThresholds ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={{ px: 4, fontWeight: 700, borderRadius: 2 }}
              >
                {savingThresholds ? 'Salvando...' : 'Salvar Escalas de Alcance'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* DIALOGS (Padronizados) */}
      <PlatformDialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <PlatformDialogTitle
          title={selectedOverride ? 'Editar Regra de Tier' : 'Nova Regra Customizada'}
          subtitle="Defina um tier fixo para um domínio ou perfil social específico."
          icon={<SettingsIcon />}
          onClose={handleCloseDialog}
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Domínio ou Rede"
              placeholder="Ex: terra.com.br ou @mensure"
              value={formData.domain || formData.username}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              fullWidth
              disabled={!!selectedOverride}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Nível de Tier</InputLabel>
              <Select
                value={formData.tier}
                label="Nível de Tier"
                onChange={(e) => setFormData({ ...formData, tier: Number(e.target.value) })}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value={1}>Tier 1 (Alto Impacto)</MenuItem>
                <MenuItem value={2}>Tier 2 (Médio Impacto)</MenuItem>
                <MenuItem value={3}>Tier 3 (Baixo Impacto)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Motivo / Justificativa"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              fullWidth
              multiline
              rows={2}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Autocomplete
              multiple
              options={availableCategories}
              value={formData.categories}
              onChange={handleDialogCategoriesChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categorias"
                  placeholder="Selecione uma ou mais categorias"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    variant="outlined"
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option === value}
              filterSelectedOptions
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Se você escolher mais de uma categoria, a principal será usada no campo legado `category` e as demais ficarão em `categories`.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={handleCloseDialog} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
            startIcon={<RefreshIcon />}
          >
            {selectedOverride ? 'Atualizar Regra' : 'Criar Regra'}
          </Button>
        </DialogActions>
      </PlatformDialog>

      <PlatformDialog 
        open={recalcDialogOpen} 
        onClose={() => setRecalcDialogOpen(false)}
        maxWidth="xs"
      >
        <PlatformDialogTitle
          title="Recalcular Tiers?"
          subtitle="Esta ação processará todos os artigos da marca."
          icon={<CalculateIcon />}
          onClose={() => setRecalcDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Deseja recalcular o tier de todos os artigos desta marca baseando-se nas novas regras e thresholds?
          </Typography>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            Pode levar alguns minutos dependendo do volume de dados.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
          <Button onClick={() => setRecalcDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleRecalculate} 
            disabled={recalculating}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            {recalculating ? <CircularProgress size={16} color="inherit" /> : 'Confirmar Recálculo'}
          </Button>
        </DialogActions>
      </PlatformDialog>

      <PlatformDialog 
        open={bulkDialogOpen} 
        onClose={() => setBulkDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Ações em Massa"
          subtitle={`Aplicar regra para ${selectedSources.length} fontes.`}
          icon={<CategoryIcon />}
          onClose={() => setBulkDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Selecione o Tier que deseja atribuir permanentemente às fontes selecionadas:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', width: '100%' }}>
              {[1, 2, 3].map(t => (
                <Button
                  key={t}
                  variant={bulkTier === t ? 'contained' : 'outlined'}
                  color={TIER_COLORS[t]}
                  onClick={() => setBulkTier(t)}
                  sx={{ 
                    flex: 1, 
                    borderRadius: 2, 
                    height: 48, 
                    fontWeight: 700,
                    boxShadow: bulkTier === t ? 'none' : undefined
                  }}
                >
                  T{t}
                </Button>
              ))}
            </Box>
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2, width: '100%' }}>
              Isso criará overrides manuais para cada fonte.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setBulkDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleBulkTierChange}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            Aplicar em Massa
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog: Delete Confirmation (Padronizado + Adicionado) */}
      <PlatformDialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Excluir Regra?"
          subtitle="A fonte voltará a usar o tier automático."
          icon={<DeleteIcon />}
          onClose={() => setDeleteDialogOpen(false)}
          color="error"
        />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Tem certeza que deseja remover a regra personalizada para <strong>{selectedOverride?.domain || selectedOverride?.username}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Remover Regra
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default TierManagement;
